import React from 'react';

// Base Skeleton Component
export const Skeleton = ({ className = '', width, height, rounded = false }) => {
  const baseClasses = `animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]`;
  const roundedClass = rounded ? 'rounded-full' : 'rounded-lg';
  
  const style = {};
  if (width) style.width = width;
  if (height) style.height = height;
  
  return (
    <div 
      className={`${baseClasses} ${roundedClass} ${className}`}
      style={style}
    />
  );
};

// Card Skeleton for dashboard cards
export const CardSkeleton = () => {
  return (
    <div className="glass-healthcare p-6 rounded-2xl border border-sky-200/50">
      <div className="animate-pulse">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Skeleton width="120px" height="24px" />
          <Skeleton width="20px" height="20px" rounded />
        </div>
        
        {/* Content lines */}
        <div className="space-y-3">
          <Skeleton width="100%" height="16px" />
          <Skeleton width="85%" height="16px" />
          <Skeleton width="70%" height="16px" />
        </div>
        
        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Skeleton width="80px" height="20px" />
            <Skeleton width="60px" height="32px" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 5 }) => {
  return (
    <tr className="border-b border-gray-100">
      {Array.from({ length: columns }).map((_, index) => (
        <td key={index} className="px-6 py-4">
          <Skeleton width="100%" height="16px" />
        </td>
      ))}
    </tr>
  );
};

// Table Skeleton with multiple rows
export const TableSkeleton = ({ rows = 5, columns = 5 }) => {
  return (
    <div className="glass-healthcare rounded-2xl border border-sky-200/50 overflow-hidden">
      {/* Table Header */}
      <div className="bg-gradient-to-r from-sky-50 to-teal-50 border-b border-sky-200 p-4">
        <div className="grid grid-cols-5 gap-4">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} width="100%" height="20px" />
          ))}
        </div>
      </div>
      
      {/* Table Body */}
      <div className="p-4">
        <div className="space-y-4">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100 last:border-b-0">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} width="100%" height="16px" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// User Profile Skeleton
export const UserProfileSkeleton = () => {
  return (
    <div className="glass-healthcare p-6 rounded-2xl border border-sky-200/50">
      <div className="flex items-center space-x-4">
        {/* Avatar */}
        <Skeleton width="64px" height="64px" rounded />
        
        {/* User Info */}
        <div className="flex-1 space-y-2">
          <Skeleton width="150px" height="20px" />
          <Skeleton width="100px" height="16px" />
          <Skeleton width="120px" height="14px" />
        </div>
        
        {/* Badge */}
        <Skeleton width="80px" height="32px" />
      </div>
    </div>
  );
};

// Stats Card Skeleton
export const StatsCardSkeleton = () => {
  return (
    <div className="glass-healthcare p-6 rounded-2xl border border-sky-200/50">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton width="80px" height="14px" />
          <Skeleton width="60px" height="32px" />
        </div>
        <Skeleton width="32px" height="32px" rounded />
      </div>
    </div>
  );
};

// Form Skeleton
export const FormSkeleton = () => {
  return (
    <div className="glass-healthcare p-8 rounded-2xl border border-sky-200/50">
      <div className="space-y-6">
        {/* Form Title */}
        <div className="text-center mb-6">
          <Skeleton width="200px" height="28px" className="mx-auto mb-2" />
          <Skeleton width="300px" height="16px" className="mx-auto" />
        </div>
        
        {/* Form Fields */}
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index}>
              <Skeleton width="100px" height="16px" className="mb-2" />
              <Skeleton width="100%" height="40px" />
            </div>
          ))}
        </div>
        
        {/* Form Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Skeleton width="80px" height="40px" />
          <Skeleton width="100px" height="40px" />
        </div>
      </div>
    </div>
  );
};

// Navigation Skeleton
export const NavigationSkeleton = () => {
  return (
    <div className="glass-healthcare border-b border-sky-200/30 shadow-xl">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <div className="flex justify-between items-center">
          {/* Brand Section */}
          <div className="flex items-center space-x-4">
            <Skeleton width="64px" height="64px" rounded />
            <div className="space-y-2">
              <Skeleton width="250px" height="24px" />
              <Skeleton width="200px" height="16px" />
            </div>
          </div>
          
          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <Skeleton width="150px" height="40px" />
            <Skeleton width="100px" height="40px" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Grid Skeleton
export const DashboardSkeleton = () => {
  return (
    <div className="space-y-8 p-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <StatsCardSkeleton key={index} />
        ))}
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - 2 cards */}
        <div className="lg:col-span-2 space-y-6">
          <CardSkeleton />
          <TableSkeleton rows={6} columns={4} />
        </div>
        
        {/* Right Column - 1 card */}
        <div className="space-y-6">
          <CardSkeleton />
          <UserProfileSkeleton />
        </div>
      </div>
    </div>
  );
};

// Page Loading Skeleton (Full page)
export const PageLoadingSkeleton = () => {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 12%, #f0f9ff 25%, #eff6ff 37%, #f8fafc 50%, #f1f5f9 62%, #f0fdfa 75%, #f6ffed 87%, #f0f9ff 100%)'
    }}>
      {/* Navigation Skeleton */}
      <NavigationSkeleton />
      
      {/* Main Content */}
      <div className="relative z-10">
        {/* Tabs Skeleton */}
        <div className="flex justify-center py-8">
          <div className="glass-healthcare p-2 rounded-2xl shadow-xl border border-sky-200/50">
            <div className="flex space-x-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} width="150px" height="48px" />
              ))}
            </div>
          </div>
        </div>
        
        {/* Dashboard Content */}
        <DashboardSkeleton />
      </div>
    </div>
  );
};


// Form Library Skeleton
export const FormLibrarySkeleton = () => {
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton width="250px" height="32px" />
          <Skeleton width="400px" height="16px" />
        </div>
        <Skeleton width="140px" height="44px" />
      </div>
      
      {/* Search and Filter */}
      <div className="flex space-x-4">
        <Skeleton width="300px" height="44px" className="flex-1" />
        <Skeleton width="150px" height="44px" />
        <Skeleton width="120px" height="44px" />
      </div>
      
      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="glass-healthcare border border-sky-200/50 rounded-2xl p-6">
            <Skeleton width="100%" height="140px" rounded className="mb-4" />
            <Skeleton width="80%" height="20px" className="mb-2" />
            <Skeleton width="60%" height="16px" className="mb-4" />
            <div className="flex justify-between items-center">
              <Skeleton width="80px" height="24px" />
              <Skeleton width="100px" height="36px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Analytics Dashboard Skeleton
export const AnalyticsSkeleton = () => {
  return (
    <div className="space-y-6 p-6">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <Skeleton width="200px" height="32px" />
        <div className="flex space-x-3">
          <Skeleton width="100px" height="40px" />
          <Skeleton width="100px" height="40px" />
          <Skeleton width="150px" height="40px" />
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatsCardSkeleton key={i} />
        ))}
      </div>
      
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-healthcare border border-sky-200/50 rounded-2xl p-6">
          <Skeleton width="180px" height="24px" className="mb-4" />
          <Skeleton width="100%" height="320px" />
        </div>
        <div className="glass-healthcare border border-sky-200/50 rounded-2xl p-6">
          <Skeleton width="180px" height="24px" className="mb-4" />
          <Skeleton width="100%" height="320px" />
        </div>
      </div>
      
      {/* Table */}
      <TableSkeleton rows={5} columns={6} />
    </div>
  );
};

// Settings Page Skeleton
export const SettingsSkeleton = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6">
      {/* Sidebar */}
      <div className="space-y-2">
        <Skeleton width="100%" height="24px" className="mb-4" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-3 p-3">
            <Skeleton width="20px" height="20px" />
            <Skeleton width="120px" height="16px" />
          </div>
        ))}
      </div>
      
      {/* Content */}
      <div className="md:col-span-3 space-y-6">
        <div className="glass-healthcare border border-sky-200/50 rounded-2xl p-6">
          <Skeleton width="200px" height="28px" className="mb-6" />
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i}>
                <Skeleton width="120px" height="16px" className="mb-2" />
                <Skeleton width="100%" height="44px" />
              </div>
            ))}
          </div>
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <Skeleton width="100px" height="44px" />
            <Skeleton width="120px" height="44px" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Skeleton
export const CalendarSkeleton = () => {
  return (
    <div className="space-y-6 p-6">
      {/* Calendar Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Skeleton width="40px" height="40px" />
          <Skeleton width="200px" height="28px" />
          <Skeleton width="40px" height="40px" />
        </div>
        <div className="flex space-x-3">
          <Skeleton width="100px" height="40px" />
          <Skeleton width="140px" height="40px" />
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="glass-healthcare border border-sky-200/50 rounded-2xl p-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} width="100%" height="32px" />
          ))}
        </div>
        
        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="aspect-square">
              <Skeleton width="100%" height="100%" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export default {
  Skeleton,
  CardSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  UserProfileSkeleton,
  StatsCardSkeleton,
  FormLibrarySkeleton,
  AnalyticsSkeleton,
  SettingsSkeleton,
  CalendarSkeleton,
  FormSkeleton,
  NavigationSkeleton,
  DashboardSkeleton,
  PageLoadingSkeleton
};